import { useSearchParams } from 'react-router-dom';
import { useData, useSession } from '@yingbaba/api';
import {
  ActionDialog,
  Button,
  ButtonLink,
  Empty,
  PageTitle,
  Pagination,
  QueryState,
  Stat,
  Status,
} from '@yingbaba/ui';
import { money, datetime, paymentFactAmount } from '@yingbaba/utils';
import type { CollectionOverview, CollectionAttention } from '@yingbaba/types';
const labels: Record<string, string> = {
  review: '到账待核查',
  pending: '开通处理中',
  unknown: '支付结果待确认',
  requested: '提现待审核',
  approved: '等待出款',
  processing: '出款处理中',
};
function destination(row: CollectionAttention) {
  if (row.kind === 'withdrawal') return '/digital-resources?tab=withdrawals';
  if (row.business === 'member')
    return `/member-management?tab=receipts&receipt_status=${row.status === 'unknown' ? '' : row.status}&receipt_order=${row.order_id}#member-receipts`;
  return `/digital-resources?tab=receipts&status=${row.status === 'unknown' ? '' : row.status}&order_id=${row.order_id}`;
}
export function CollectionWorkbench() {
  const role = useSession().data?.account.platform_role || '';
  const write = ['super_admin', 'finance'].includes(role);
  const [params, setParams] = useSearchParams();
  const filter = ['review', 'pending', 'unknown', 'withdrawal'].includes(params.get('filter') || '')
    ? params.get('filter')!
    : 'all';
  const page = Math.min(10000, Math.max(1, Math.floor(Number(params.get('page')) || 1)));
  const q = useData<CollectionOverview>(
    `/admin/collection-overview?filter=${filter}&page=${page}`,
    write || role === 'auditor',
    30000,
  );
  const choose = (value: string) => setParams(value === 'all' ? {} : { filter: value });
  return (
    <>
      <PageTitle
        title="财务工作台"
        description="优先处理已到账异常、支付核验和数字资源提现。"
        action={
          <ButtonLink to="/payment-settings" variant="secondary">
            支付方式
          </ButtonLink>
        }
      />
      <QueryState query={q}>
        <div className="stats collection-workbench-stats">
          {[
            ['review', '到账待核查', q.data?.review_count || 0, '核对资金事实与处理依据'],
            ['pending', '开通处理中', q.data?.pending_count || 0, '已到账，等待开通或签发授权'],
            ['unknown', '支付结果未知', q.data?.unknown_count || 0, '核验渠道结果，避免重复收款'],
            [
              'withdrawal',
              '提现待办',
              q.data?.withdrawal_count || 0,
              `申请总额 ${money(q.data?.withdrawal_minor || 0)}`,
            ],
          ].map(([key, label, value, foot]) => (
            <button
              className="collection-stat"
              key={String(key)}
              onClick={() => choose(String(key))}
              aria-pressed={filter === key}
            >
              <Stat label={String(label)} value={Number(value)} foot={String(foot)} />
            </button>
          ))}
        </div>
        <section className="panel" id="collection-attention">
          <div className="panel-title">
            <h2>待处理事项</h2>
            <Button variant="secondary" onClick={() => void q.refetch()}>
              刷新
            </Button>
          </div>
          <div className="row wrap collection-attention-filters">
            {[
              ['all', '全部待办'],
              ['review', '到账待核查'],
              ['pending', '开通处理中'],
              ['unknown', '支付结果未知'],
              ['withdrawal', '提现待办'],
            ].map(([value, label]) => (
              <Button
                key={value}
                variant={filter === value ? 'primary' : 'secondary'}
                onClick={() => choose(value)}
              >
                {label}
              </Button>
            ))}
          </div>
          {!q.data?.items?.length && (
            <Empty
              title="当前没有这类待办"
              description="新的到账异常、支付核验和提现申请会在这里汇总。"
            />
          )}
          {q.data?.items?.map((row) => (
            <article
              className="collection-attention-item"
              key={`${row.business}:${row.kind}:${row.id}`}
            >
              <div className="grow">
                <div className="row wrap">
                  <strong>
                    {row.business === 'member' ? '付费会员' : '数字资源'} ·{' '}
                    {paymentFactAmount(row.amount_minor, row.currency)}
                  </strong>
                  <Status value={row.status} label={labels[row.status] || '待处理'} />
                </div>
                <p className="small muted">
                  {datetime(row.created_at)} · {row.kind === 'withdrawal' ? '提现编号' : '订单编号'}
                  ：{row.order_id || row.id}
                </p>
                {row.code === 'PROVIDER_REFUND_DETECTED' && (
                  <p className="small">渠道提示发生退款，请先核对退款事实与凭证。</p>
                )}
              </div>
              <div className="row wrap">
                {row.kind === 'payment' && write && (
                  <ActionDialog
                    title="核验支付结果"
                    trigger="核验渠道状态"
                    path={`/admin/collection-payments/${row.business}/${row.id}/refresh`}
                    submitLabel="发起核验"
                    description="向原商户查询结果，必要时确认关单；不会创建替代支付或修改原订单金额。"
                    fields={[{ name: 'reason', label: '核验说明', min: 4, max: 500 }]}
                  />
                )}
                {row.kind !== 'payment' && (
                  <ButtonLink to={destination(row)} variant="secondary">
                    查看处理
                  </ButtonLink>
                )}
              </div>
            </article>
          ))}
          <Pagination
            page={page}
            size={12}
            total={q.data?.total || 0}
            onPage={(value) =>
              setParams({ ...(filter === 'all' ? {} : { filter }), page: String(value) })
            }
          />
        </section>
        <div className="row wrap">
          <ButtonLink to="/digital-resources?tab=receipts" variant="secondary">
            数字资源收款
          </ButtonLink>
          <ButtonLink to="/member-management#member-receipts" variant="secondary">
            会员收款
          </ButtonLink>
          <ButtonLink to="/digital-resources?tab=withdrawals" variant="secondary">
            数字资源提现
          </ButtonLink>
        </div>
      </QueryState>
    </>
  );
}
